import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Calendar,
  Camera,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ClipboardCheck,
  FileText,
  MapPin,
  Play,
  Upload,
} from 'lucide-react'
import {
  getVisitChecklist,
  getVisitDetails,
  transitionVisit,
  updateVisit,
  type Visit,
  type VisitTransitionTarget,
} from '../../lib/services/visits'
import { ScoringSection } from '../../components/visits/ScoringSection'
import { authFailureMessage, isAuthFailure, shouldRetryRequest } from '../../lib/errors'
import { useAuth, type ViewerContext } from '../../context/AuthContext'
import { useState } from 'react'
import { getFieldAppUrl } from '../../lib/fieldAppHost'

/** Admin + editor (Surveyor) match BE requireRole for PATCH/transition (#1854). */
function canManageVisitLifecycle(viewer: ViewerContext | null | undefined): boolean {
  if (!viewer) return false
  if (viewer.is_superadmin || viewer.is_backend_superadmin) return true
  return viewer.role === 'Admin' || viewer.role === 'Surveyor'
}

function canReleaseVisit(viewer: ViewerContext | null | undefined): boolean {
  if (!viewer) return false
  if (viewer.is_superadmin || viewer.is_backend_superadmin) return true
  return viewer.role === 'Admin'
}

function toDateInputValue(isoOrDate: string): string {
  if (!isoOrDate) return ''
  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoOrDate)) return isoOrDate
  const parsed = new Date(isoOrDate)
  if (Number.isNaN(parsed.valueOf())) return isoOrDate.slice(0, 10)
  return parsed.toISOString().slice(0, 10)
}

function isMutableVisit(status: Visit['status']): boolean {
  return status !== 'released' && status !== 'archived'
}

/**
 * Visit Details — Survey-First Layout
 *
 * Leads with the survey workflow (walk → capture → findings → complete)
 * and positions the standards checklist as a secondary reference tool.
 * #1854: admin/editor lifecycle (reschedule + status transition) on existing BE routes.
 */
export function VisitDetailsPage() {
  const { visitId } = useParams()
  const { signOut, viewerContext } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showChecklist, setShowChecklist] = useState(false)
  /** null = use server visit_date; scoped by visitId so navigation does not leak drafts. */
  const [rescheduleDraft, setRescheduleDraft] = useState<{ visitId: string; date: string } | null>(null)
  const [lifecycleMessage, setLifecycleMessage] = useState<string | null>(null)
  const [lifecycleError, setLifecycleError] = useState<string | null>(null)

  const {
    data: visit,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['visits', visitId],
    queryFn: () => getVisitDetails(visitId!),
    enabled: !!visitId,
    retry: shouldRetryRequest,
  })

  const { data: checklistItems, error: checklistError, isLoading: isChecklistLoading } = useQuery({
    queryKey: ['visit-checklist', visitId],
    queryFn: () => getVisitChecklist(visitId!),
    enabled: !!visitId,
    retry: shouldRetryRequest,
  })

  const serverDate = visit?.visit_date ? toDateInputValue(visit.visit_date) : ''
  const rescheduleDate =
    rescheduleDraft && visitId && rescheduleDraft.visitId === visitId
      ? rescheduleDraft.date
      : serverDate

  const invalidateVisit = async () => {
    await queryClient.invalidateQueries({ queryKey: ['visits', visitId] })
    await queryClient.invalidateQueries({ queryKey: ['visits', 'assignments'] })
  }

  const updateMutation = useMutation({
    mutationFn: (surveyDate: string) => updateVisit(visitId!, { survey_date: surveyDate }),
    onSuccess: async () => {
      setLifecycleError(null)
      setLifecycleMessage('Visit date updated.')
      setRescheduleDraft(null)
      await invalidateVisit()
    },
    onError: (err: unknown) => {
      setLifecycleMessage(null)
      setLifecycleError(err instanceof Error ? err.message : 'Failed to update visit')
    },
  })

  const transitionMutation = useMutation({
    mutationFn: (target: VisitTransitionTarget) => transitionVisit(visitId!, target),
    onSuccess: async (_data, target) => {
      setLifecycleError(null)
      setLifecycleMessage(
        target === 'archived'
          ? 'Visit archived.'
          : target === 'released'
            ? 'Visit released.'
            : target === 'in_review'
              ? 'Visit submitted for review.'
              : 'Visit status updated.',
      )
      await invalidateVisit()
    },
    onError: (err: unknown) => {
      setLifecycleMessage(null)
      setLifecycleError(err instanceof Error ? err.message : 'Failed to transition visit')
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-neutral-900" />
      </div>
    )
  }

  if (error || !visit) {
    const isAuthError = isAuthFailure(error)
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
        <p className="font-medium">{isAuthError ? 'Session validation failed' : 'Failed to load visit details'}</p>
        <p className="text-sm">{isAuthError ? authFailureMessage(error) : (error as Error | undefined)?.message ?? 'Visit not found'}</p>
        {isAuthError ? (
          <button
            className="mt-2 inline-block text-sm underline"
            onClick={async () => {
              await signOut()
              navigate('/login')
            }}
          >
            Sign in again
          </button>
        ) : (
          <Link to="/visits" className="mt-2 inline-block text-sm underline">
            Back to assignments
          </Link>
        )}
      </div>
    )
  }

  const standards = checklistItems || []
  const compliantCount = 0
  const statusLabel = visit.status.replace('_', ' ')
  const statusClass =
    visit.status === 'in_progress' || visit.status === 'in_review'
      ? 'badge-in-progress'
      : visit.status === 'released'
      ? 'badge-released'
      : visit.status === 'archived'
      ? 'badge-archived'
      : 'badge-scheduled'

  const showLifecycle = canManageVisitLifecycle(viewerContext)
  const mutable = isMutableVisit(visit.status)
  const busy = updateMutation.isPending || transitionMutation.isPending
  const canSubmitReview =
    visit.status === 'scheduled' || visit.status === 'in_progress'
  const canRelease = visit.status === 'in_review' && canReleaseVisit(viewerContext)
  const canArchive = visit.status !== 'archived'

  return (
    <div className="mx-auto max-w-5xl space-y-5 pb-16">
      {/* Header */}
      <header className="v11-workspace-header">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-[var(--brand-secondary)]">
              Selected facility visit
            </p>
            <h2 className="font-brand text-2xl font-bold leading-tight text-[var(--brand-deep)]">{visit.facility_name}</h2>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm font-medium text-slate-500">
              <span className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5">
                <Calendar size={14} />
                {visit.visit_date}
              </span>
              <span className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5">
                <MapPin size={14} />
                {visit.address}
              </span>
            </div>
          </div>
          <div className={statusClass}>{statusLabel}</div>
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <Link to="survey/documents" className="btn-ghost px-4 py-2 text-sm">
            <FileText size={14} className="mr-1.5 inline" />
            Review Prestudy Uploads
          </Link>
          <Link to="prestudy/create" className="btn-ghost px-4 py-2 text-sm">
            <Upload size={14} className="mr-1.5 inline" />
            Request Uploads
          </Link>
        </div>
      </header>

      {/* Primary Action: Begin/Continue Survey */}
      <section className="v11-section border-2 border-[var(--brand-primary)] p-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="flex items-center gap-2 text-lg font-black text-[var(--brand-deep)]">
              <Camera size={22} className="text-[var(--brand-primary)]" />
              Mock Survey
            </h3>
            <p className="mt-1 text-sm text-slate-600">
              Walk the facility, capture photos and voice observations, document findings by area, and generate an AI-powered compliance report.
            </p>
          </div>
          <a
            href={getFieldAppUrl(`/visits/${visitId}/survey`, { resumeFacility: visit.facility_name })}
            className="btn-primary flex items-center gap-2 whitespace-nowrap px-6 py-3 text-sm font-bold"
          >
            <Play size={16} />
            Begin Survey
          </a>
        </div>
      </section>

      {/* Review & Release */}
      <section className="v11-section p-5">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h3 className="flex items-center gap-2 text-base font-black text-slate-900">
              <FileText size={18} className="text-[#1a4f7c]" />
              Review & Release
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Review all findings before releasing to the facility for response.
            </p>
          </div>
          <Link to="review" className="btn-primary px-5 py-2.5 text-sm">
            Review Findings
          </Link>
        </div>
      </section>

      {/* Lifecycle — reschedule + status transitions (#1854) */}
      {showLifecycle && (
        <section className="v11-section p-5" data-testid="visit-lifecycle-panel">
          <div className="flex flex-col gap-1">
            <h3 className="flex items-center gap-2 text-base font-black text-slate-900">
              <Calendar size={18} className="text-[#1a4f7c]" />
              Visit lifecycle
            </h3>
            <p className="text-sm text-slate-500">
              Reschedule the survey date or move this visit through review, release, and archive. Uses existing visits PATCH and transition routes.
            </p>
          </div>

          {lifecycleMessage && (
            <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              {lifecycleMessage}
            </div>
          )}
          {lifecycleError && (
            <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {lifecycleError}
            </div>
          )}

          {mutable ? (
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
              <label className="block flex-1">
                <span className="text-xs font-black uppercase tracking-widest text-slate-400">Survey date</span>
                <input
                  type="date"
                  value={rescheduleDate}
                  onChange={(event) =>
                    setRescheduleDraft({ visitId: visitId!, date: event.target.value })
                  }
                  disabled={busy}
                  className="mt-1 block w-full rounded-xl border border-[var(--brand-border)] bg-white px-3 py-2.5 text-sm font-semibold text-slate-800 outline-none focus:border-[var(--brand-primary)] focus:ring-2 focus:ring-blue-100"
                  data-testid="visit-reschedule-date"
                />
              </label>
              <button
                type="button"
                className="btn-primary px-5 py-2.5 text-sm disabled:opacity-50"
                disabled={busy || !rescheduleDate || rescheduleDate === serverDate}
                onClick={() => updateMutation.mutate(rescheduleDate)}
                data-testid="visit-reschedule-submit"
              >
                {updateMutation.isPending ? 'Saving…' : 'Reschedule'}
              </button>
            </div>
          ) : (
            <p className="mt-3 text-sm text-slate-500">
              Released and archived visits cannot be rescheduled.
            </p>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            {canSubmitReview && (
              <button
                type="button"
                className="btn-ghost px-4 py-2 text-sm disabled:opacity-50"
                disabled={busy}
                onClick={() => transitionMutation.mutate('in_review')}
                data-testid="visit-transition-in-review"
              >
                Submit for review
              </button>
            )}
            {canRelease && (
              <button
                type="button"
                className="btn-primary px-4 py-2 text-sm disabled:opacity-50"
                disabled={busy}
                onClick={() => transitionMutation.mutate('released')}
                data-testid="visit-transition-release"
              >
                Release visit
              </button>
            )}
            {canArchive && (
              <button
                type="button"
                className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-bold text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                disabled={busy}
                onClick={() => {
                  if (
                    window.confirm(
                      visit.status === 'released'
                        ? 'Archive this released visit? This cannot be undone from the UI.'
                        : 'Archive (cancel) this visit? It will no longer be editable.',
                    )
                  ) {
                    transitionMutation.mutate('archived')
                  }
                }}
                data-testid="visit-transition-archive"
              >
                {visit.status === 'released' ? 'Archive' : 'Archive / cancel'}
              </button>
            )}
          </div>
        </section>
      )}

      {/* Scoring */}
      <ScoringSection compliantCount={compliantCount} totalCount={standards.length} />

      {/* Standards Checklist — Collapsible Secondary Section */}
      <section className="v11-section p-5">
        <button
          onClick={() => setShowChecklist(!showChecklist)}
          className="flex w-full items-center justify-between text-left"
        >
          <h3 className="flex items-center gap-2 text-base font-black text-slate-900">
            <ClipboardCheck size={18} className="text-[#1a4f7c]" />
            Standards Checklist
            <span className="text-xs font-normal text-slate-400">
              ({standards.length} standards)
            </span>
          </h3>
          {showChecklist ? <ChevronUp size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
        </button>
        <p className="mt-1 text-sm text-slate-500">
          Reference checklist for compliance verification against specific standards.
        </p>

        {showChecklist && (
          <div className="mt-4 space-y-3">
            {Boolean(checklistError) && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-red-700">
                <p className="text-sm font-medium">
                  {isAuthFailure(checklistError) ? 'Session validation failed for checklist' : 'Failed to load checklist'}
                </p>
                <p className="text-xs">
                  {isAuthFailure(checklistError) ? authFailureMessage(checklistError) : (checklistError as Error).message}
                </p>
              </div>
            )}
            {isChecklistLoading && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-600">
                <p className="text-sm">Loading standards...</p>
              </div>
            )}
            {!isChecklistLoading && standards.length === 0 && (
              <div className="rounded-xl border border-dashed border-slate-200 p-4 text-sm text-slate-500">
                No standards assigned for this visit yet.
              </div>
            )}
            {standards.map((standard) => (
              <a
                key={standard.template_id}
                href={getFieldAppUrl(`/visits/${visitId}/standard/${standard.template_id}`)}
                className="group flex items-center justify-between rounded-xl border border-slate-200 p-4 transition-all hover:border-[#1a4f7c]"
              >
                <div>
                  <p className="text-xs font-black uppercase tracking-widest text-slate-400">{standard.standard_id}</p>
                  <p className="font-bold text-slate-900 group-hover:text-[#1a4f7c]">{standard.question_text}</p>
                </div>
                <ChevronRight size={18} className="text-slate-300 group-hover:text-[#1a4f7c]" />
              </a>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
